import React, { memo } from 'react';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { PlayerPositionsFilters } from '@/feature/lobby/components/PlayerPositionFilters';
import { PlayerProjectionsFilters } from '@/feature/lobby/components/PlayerProjectionsFilters';
import { FilterType, Filters, isFilterBarHidden } from '@/feature/lobby/utils/filters';
import { MaxWidthWrapper } from '@/feature/responsive-design/WebComponents';
import { LeaguePositionFilters } from '@/hooks/use-fantasy-league-configs';
import { League } from '@/types/api.generated';

/**
 * A component that renders either position or projection filters for players
 * based on the provided filter type and league.
 *
 * @param league league the filters belong to, used to remount the rows on a league change
 * @param filterType Position or Projection
 * @param projectionFiltersData pre-computed projection filters data
 * @param positionFilters pre-computed position filters
 */

export const PLAYER_FILTERS_HEIGHT = 68;
export const PlayersFilters = memo(
    ({
        league,
        filterType,
        projectionFiltersData,
        positionFilters,
    }: {
        league: League;
        filterType?: FilterType;
        projectionFiltersData: PlayerProjectionFragment[];
        positionFilters: LeaguePositionFilters;
    }) => {
        const hasPositionFilters = positionFilters && positionFilters.length > 0;

        if (!filterType) {
            return null;
        }

        if (isFilterBarHidden(filterType, hasPositionFilters)) {
            return null;
        }

        return (
            <MaxWidthWrapper flex={1} minHeight={PLAYER_FILTERS_HEIGHT}>
                {filterType === Filters.Projection ? (
                    <PlayerProjectionsFilters key={`filters_${league}`} filtersData={projectionFiltersData} />
                ) : null}
                {filterType === Filters.Position ? (
                    <PlayerPositionsFilters key={`filters_${league}`} positionFilters={positionFilters} />
                ) : null}
            </MaxWidthWrapper>
        );
    }
);
